import type { PostFormat } from '@str/shared';
import { checkPhoto } from './images';

/** Decode metadata locally before any bytes leave the browser. */
export async function checkComposerFile(file: File, format: PostFormat): Promise<void> {
  if (!file.type.startsWith('video/')) {
    if (!file.type.startsWith('image/')) throw new Error('Choose a photo or an MP4 Reel.');
    const check = await checkPhoto(file);
    if (!check.ok) throw new Error(check.reason);
    return;
  }
  if (format !== 'Reel') throw new Error('Choose Reel to use a video.');
  await readReelClip(file, 3000);
}

export async function readReelClip(file: File, minimumMs = 1000): Promise<number> {
  if (file.type !== 'video/mp4')
    throw new Error('Choose an MP4 clip. Export this video as MP4 first.');
  if (file.size > 200 * 1024 * 1024) throw new Error('Video must be 200 MB or smaller.');
  const duration = await new Promise<number>((resolve, reject) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    const cleanup = () => {
      clearTimeout(timer);
      URL.revokeObjectURL(url);
      video.removeAttribute('src');
    };
    const failed = () => {
      cleanup();
      reject(new Error('Could not read this clip. Export it as MP4 and try again.'));
    };
    const timer = setTimeout(failed, 10_000);
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      const seconds = video.duration;
      cleanup();
      resolve(seconds);
    };
    video.onerror = failed;
    video.src = url;
  });
  if (!Number.isFinite(duration) || duration <= 0)
    throw new Error('Could not read this clip’s length. Export it as MP4 and try again.');
  if (duration * 1000 < minimumMs)
    throw new Error(`Choose a clip at least ${minimumMs / 1000} seconds long.`);
  return Math.floor(duration * 1000);
}
